import React, { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
} from "../../../api/users";
import { InputField } from "../../../components/forms/InputField";
import { Card } from "../../../components/layout/Card";
import {
  ErrorWrapper,
  IconContainer,
  InputWrapper,
} from "../questionnaireInputs/SharedFormStyles";
import Loader from "../../../components/icons/Loader";
import useIsMobile from "../../../hooks/useIsMobile";
import RequiredStar from "../../../components/RequiredAsterisk";
import {
  validateDateOfBirth,
  formatDateForHTMLInput,
} from "../../../utils/dateUtils";
import GreenTick from "../../../components/icons/GreenTick";
import { IconSize } from "../../../components/theme";
import { CardContainer } from "../Question";

const userBirthDateQuestionConfig = {
  title: "What is your date of birth?",
  isRequired: true,
};

const UserBirthDate = ({
  shouldValidateQuestionnaireSection = false,
}: {
  shouldValidateQuestionnaireSection?: boolean;
}) => {
  const isMobile = useIsMobile();

  const { data: user } = useGetCurrentUserQuery();
  const [
    updateUser,
    { isLoading, isError, isSuccess: isUpdateSuccess, reset },
  ] = useUpdateUserProfileMutation();

  const [dateInput, setDateInput] = useState<string>(
    user?.birthdate ? formatDateForHTMLInput(user.birthdate) : "",
  );
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setDateInput(
        user.birthdate ? formatDateForHTMLInput(user.birthdate) : "",
      );
    }
  }, [user]);

  const save = useDebouncedCallback((value: string | null) => {
    if (!user?.id) return;

    void updateUser({
      id: user.id,
      birthdate: value ? new Date(value) : null,
    });
  }, 1000);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    const value = e.target.value;
    setDateInput(value);

    const validationError = validateDateOfBirth(value);
    setDateError(validationError);

    if (validationError) {
      save(null);
      return;
    }
    save(value);
  };

  const validationError = !!(!dateInput && shouldValidateQuestionnaireSection);
  const isSuccess = isUpdateSuccess && !isLoading;

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {userBirthDateQuestionConfig.title}
              {userBirthDateQuestionConfig.isRequired && <RequiredStar />}
            </>
          ),
        }}
        isError={validationError}
      >
        <InputWrapper>
          <InputField
            type="date"
            placeholder="mm/dd/yyyy"
            value={dateInput}
            onChange={handleDateChange}
            required={userBirthDateQuestionConfig.isRequired}
            errorMessage={dateError}
          />
          {isLoading && (
            <IconContainer>
              <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
            </IconContainer>
          )}
          {isSuccess && (
            <IconContainer>
              <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
            </IconContainer>
          )}
        </InputWrapper>

        {isError && (
          <ErrorWrapper>
            Error saving answer, please refresh the page and try again
          </ErrorWrapper>
        )}
      </Card>
    </CardContainer>
  );
};

export default UserBirthDate;
